import React, { useState, useRef, useEffect } from "react";
import "./Query.css";

const API    = process.env.REACT_APP_API_URL;
const SITES  = [
  { site_id: 5,  label: "Planter 006" },
  { site_id: 11, label: "Planter 007A" },
];
const EXAMPLES = [
  "How many events occurred each month?",
  "Which events had efficiency below 90%?",
  "What is the highest peak intensity recorded?",
  "Show average soil moisture at 10cm for each month in 2024",
  "Which event had the longest antecedent dry period?",
];

function ResultTable({ columns, rows }) {
  if (!columns.length) return null;
  return (
    <div className="nl-table-wrap">
      <table className="nl-table">
        <thead>
          <tr>{columns.map(c => <th key={c}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => <td key={j}>{cell ?? "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AssistantMessage({ msg }) {
  const [sqlOpen, setSqlOpen] = useState(false);

  if (msg.error) {
    return (
      <div className="nl-msg nl-msg-assistant nl-msg-error">
        <div className="nl-error-text">{msg.error}</div>
        {msg.explanation && <div className="nl-explanation">{msg.explanation}</div>}
      </div>
    );
  }

  return (
    <div className="nl-msg nl-msg-assistant">
      {msg.sql && (
        <div className="nl-sql-block">
          <button className="nl-sql-toggle" onClick={() => setSqlOpen(v => !v)}>
            SQL {sqlOpen ? "▲" : "▼"}
          </button>
          {sqlOpen && <pre className="nl-sql-pre">{msg.sql}</pre>}
        </div>
      )}

      {msg.row_count > 0 ? (
        <>
          <ResultTable columns={msg.columns} rows={msg.rows} />
          {msg.row_count === 200 && (
            <div className="nl-row-cap">Showing first 200 rows.</div>
          )}
        </>
      ) : (
        <div className="nl-empty">No rows returned.</div>
      )}

      {msg.explanation && (
        <div className="nl-explanation">{msg.explanation}</div>
      )}
    </div>
  );
}

export default function Query() {
  const [siteId, setSiteId]   = useState(5);
  const [input, setInput]     = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  async function handleSend(question) {
    const q = (question || input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: q }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/nl-query`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: q, site_id: siteId }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: "assistant", ...data }]);
    } catch {
      setMessages(prev => [...prev, {
        role: "assistant",
        error: "Could not reach the API. Is the backend running?",
        sql: null, columns: [], rows: [], row_count: 0, explanation: "",
      }]);
    } finally {
      setLoading(false);
    }
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }

  return (
    <div className="nl-page">
      <div className="nl-header">
        <div>
          <h2 className="nl-title">Natural Language Query</h2>
          <p className="nl-subtitle">Ask questions about your SuDS data in plain English.</p>
        </div>
        <select
          className="nl-site-select"
          value={siteId}
          onChange={e => setSiteId(Number(e.target.value))}
        >
          {SITES.map(s => (
            <option key={s.site_id} value={s.site_id}>{s.label}</option>
          ))}
        </select>
      </div>

      <div className="nl-messages">
        {messages.length === 0 && !loading && (
          <div className="nl-welcome">
            <div className="nl-welcome-title">Try asking…</div>
            <div className="nl-examples">
              {EXAMPLES.map(ex => (
                <button key={ex} className="nl-example" onClick={() => handleSend(ex)}>
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) =>
          msg.role === "user" ? (
            <div key={i} className="nl-msg nl-msg-user">{msg.text}</div>
          ) : (
            <AssistantMessage key={i} msg={msg} />
          )
        )}

        {loading && (
          <div className="nl-msg nl-msg-assistant nl-thinking">
            <span className="nl-dot" /><span className="nl-dot" /><span className="nl-dot" />
            <span className="nl-thinking-label">Generating SQL… (may take ~2 min)</span>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <div className="nl-input-row">
        <textarea
          className="nl-input"
          rows={2}
          placeholder="Ask a question about events, soil moisture, temperature…"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          disabled={loading}
        />
        <button
          className="nl-send"
          onClick={() => handleSend()}
          disabled={!input.trim() || loading}
        >
          Send
        </button>
      </div>
      <div className="nl-footer">
        Powered by Ollama · mistral-nemo:12b · queries take ~1–2 min on first use · results capped at 200 rows
      </div>
    </div>
  );
}
